"use client";

import Link, { useLinkStatus } from "next/link";
import { useDayOut } from "@/app/_components/companion/DayOutSelection";
import { withDayOut } from "@/lib/companion/day-out";
import type { ReactNode } from "react";
import {
  CalendarDays,
  CircleUserRound,
  MapPinned,
  Compass,
  UsersRound,
} from "lucide-react";
import { usePathname } from "next/navigation";
import {
  COMPANION_DESTINATIONS,
  companionDestinationForPathname,
  type CompanionDestinationId,
} from "@/lib/companion/navigation";
import { companionDestinationOpenedEvent } from "@/lib/companion/analytics-events";
import { CompanionTrackedLink } from "./CompanionAnalytics";

const ICONS = {
  today: CalendarDays,
  plan: MapPinned,
  explore: Compass,
  people: UsersRound,
  you: CircleUserRound,
} as const;

export function CompanionNavigation({
  activeDestination,
  viewedBaseId,
  analyticsEnabled = true,
}: {
  activeDestination: CompanionDestinationId;
  viewedBaseId?: string | null;
  analyticsEnabled?: boolean;
}) {
  const pathname = usePathname();
  const { selection } = useDayOut();
  const active = companionDestinationForPathname(pathname) ?? activeDestination;

  return (
    <nav aria-label="Companion destinations">
      <div className="hidden border-t border-white/10 min-[760px]:grid min-[760px]:grid-cols-5 min-[760px]:gap-1">
        {COMPANION_DESTINATIONS.map((destination) => {
          const current = destination.id === active;
          const href = withDayOut(destinationHref(destination.href, viewedBaseId), selection);
          return navigationLink({
            analyticsEnabled,
            destinationId: destination.id,
            href,
            current,
            className: current
              ? "relative inline-flex min-h-[48px] items-center justify-center gap-2 rounded-t-sm border-b-2 border-teal-light bg-white/5 px-3 text-sm font-semibold text-white no-underline"
              : "relative inline-flex min-h-[48px] items-center justify-center gap-2 rounded-t-sm border-b-2 border-transparent px-3 text-sm font-semibold text-white/70 no-underline hover:text-white",
            children: <><DestinationIcon destination={destination.id} />{destination.label}<NavigationPending label={destination.label} /></>,
          });
        })}
      </div>

      <div
        data-companion-mobile-nav
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-border bg-white/95 px-1 pt-2 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(22,35,59,0.10)] backdrop-blur min-[760px]:hidden"
      >
        {COMPANION_DESTINATIONS.map((destination) => {
          const Icon = ICONS[destination.id];
          const current = destination.id === active;
          const href = withDayOut(destinationHref(destination.href, viewedBaseId), selection);
          return navigationLink({
            analyticsEnabled,
            destinationId: destination.id,
            href,
            current,
            className: current
              ? "relative flex min-h-[48px] min-w-[44px] flex-col items-center justify-center gap-1 rounded-md bg-teal/10 text-[11px] font-bold text-teal-dark no-underline"
              : "relative flex min-h-[48px] min-w-[44px] flex-col items-center justify-center gap-1 rounded-md text-[11px] font-semibold text-text-muted no-underline hover:text-navy",
            children: (
              <>
                <Icon className="h-5 w-5" aria-hidden="true" />
                <span>{destination.label}</span>
                <NavigationPending label={destination.label} />
              </>
            ),
          });
        })}
      </div>
    </nav>
  );
}

function navigationLink({
  analyticsEnabled,
  destinationId,
  href,
  current,
  className,
  children,
}: {
  analyticsEnabled: boolean;
  destinationId: CompanionDestinationId;
  href: string;
  current: boolean;
  className: string;
  children: ReactNode;
}) {
  const sharedProps = {
    href,
    "aria-current": current ? ("page" as const) : undefined,
    className: `${className} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal min-[760px]:focus-visible:ring-teal-light`,
  };

  if (!analyticsEnabled) {
    return (
      <Link key={destinationId} {...sharedProps}>
        {children}
      </Link>
    );
  }
  return (
    <CompanionTrackedLink
      key={destinationId}
      {...sharedProps}
      event={companionDestinationOpenedEvent(destinationId)}
    >
      {children}
    </CompanionTrackedLink>
  );
}

function destinationHref(href: string, viewedBaseId: string | null | undefined): string {
  if (!viewedBaseId) return href;
  return `${href}?${new URLSearchParams({ base: viewedBaseId }).toString()}`;
}

function DestinationIcon({ destination }: { destination: CompanionDestinationId }) {
  const Icon = ICONS[destination];
  return <Icon className="size-4" aria-hidden="true" />;
}

/** Keep the page in place while its destination loads, with local, non-shifting feedback. */
function NavigationPending({ label }: { label: string }) {
  const { pending } = useLinkStatus();
  if (!pending) return null;
  return (
    <span role="status" className="pointer-events-none absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-teal-light motion-safe:animate-pulse">
      <span className="sr-only">Loading {label}</span>
    </span>
  );
}
